import React, { useState } from "react";
import { Box, Collapse, Typography, Divider, CircularProgress } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

import { CardLayout, EmptyState, IconButton } from ".";

interface Item {
  date?: string;
  name: string;
  value?: string | number;
  [key: string]: any;
}

interface ButtonProps {
  icon: React.ReactNode;
  buttonText?: string;
  tooltip?: string;
  onClick: (item?: any) => void;
  hoverStyles?: any;
}

interface EmptyStateProps {
  title: string;
  subtitle: string;
}

interface CollapsibleCardProps {
  loading?: boolean;
  title?: string;
  icon?: React.ReactNode;
  items?: Item[];
  headerButtons?: ButtonProps[];
  itemButtons?: ButtonProps[];
  emptyState?: EmptyStateProps;
}

/**
 * A card component that displays collapsible content.
 */
export const CollapsibleCard = ({
  loading = false,
  title = "",
  icon,
  items = [],
  headerButtons = [],
  itemButtons = [],
  emptyState = { title: "No data found", subtitle: "Try adding a new record to get started!" },
}: CollapsibleCardProps) => {
  const [open, setOpen] = useState(false);

  return (
    <CardLayout sx={{ margin: "11px" }}>
      {/* header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        {/* left side */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }} onClick={() => setOpen(!open)}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "2px solid #674BF5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#674BF5",
            }}
          >
            {icon}
          </Box>
          <Typography fontWeight={600} color="#674BF5" fontSize={16}>
            {title}
          </Typography>
        </Box>

        {/* right side */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {headerButtons.map((button, index) => {
            if (!button) return null;
            return (
              <IconButton
                key={index}
                icon={button?.icon}
                buttonText={button?.buttonText}
                tooltip={button?.tooltip}
                onClick={button?.onClick}
                styles={{
                  color: "#919191",
                  backgroundColor: "#F2F2F2",
                  fontSize: "12px",
                  padding: "3px 8px",
                }}
                hoverStyles={{ backgroundColor: "#ecddfdff" }}
              />
            );
          })}
          <IconButton
            icon={open ? <ExpandLess /> : <ExpandMore />}
            tooltip={open ? "Close" : "Open"}
            onClick={() => setOpen(!open)}
            styles={{ color: "#919191", height: 28, width: 28, minWidth: 28, borderRadius: "50%" }}
            hoverStyles={{ backgroundColor: "#ecddfdff" }}
          />
        </Box>
      </Box>

      {/* expandable content */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#674BF5" }} />
            </Box>
          ) : items.length > 0 ? (
            items.map((item, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {/* left */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {item.date && (
                      <Box
                        sx={{
                          backgroundColor: "#E0E0E0",
                          borderRadius: 3,
                          px: 1.5,
                          py: 0.5,
                          fontSize: 12,
                        }}
                      >
                        {item.date}
                      </Box>
                    )}
                    <Typography fontSize={14}>{item.name}</Typography>
                  </Box>

                  {/* right */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {itemButtons.map((button, index) => (
                      <IconButton
                        key={index}
                        icon={button?.icon}
                        buttonText={button?.buttonText}
                        tooltip={button?.tooltip}
                        onClick={() => button?.onClick(item)}
                        styles={{
                          color: "#674BF5",
                          borderColor: "#ecddfdff",
                          height: 28,
                          width: 28,
                          minWidth: 28,
                          borderRadius: "50%",
                        }}
                        hoverStyles={{ backgroundColor: "#ecddfdff" }}
                      />
                    ))}
                    {item?.value && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 32,
                          minHeight: 32,
                          px: 2,
                          py: 0.5,
                          borderRadius: "16px",
                          backgroundColor: "#EDE7F6",
                          color: "#7E57C2",
                          fontWeight: 600,
                          fontSize: 14,
                          transition: "min-width 0.2s, width 0.2s",
                        }}
                      >
                        {item.value}
                      </Box>
                    )}
                  </Box>
                </Box>
                {index < items.length - 1 && <Divider sx={{ my: 1, borderColor: "#797979" }} />}
              </Box>
            ))
          ) : (
            <EmptyState title={emptyState?.title} subtitle={emptyState?.subtitle} />
          )}
        </Box>
      </Collapse>
    </CardLayout>
  );
};
